import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { 
  FaMobileAlt, 
  FaGooglePlay, 
  FaApple, 
  FaPaintBrush, 
  FaCode, 
  FaRocket,
  FaBug,
  FaChartLine
} from 'react-icons/fa';

// Framer CSS styles (unchanged)
const framerStyles = `...`; // (keep your existing framerStyles here)

const Services = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, threshold: 0.2 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth <= 768);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  const MotionOrDiv = isMobile ? 'div' : motion.div;

  const services = [
    {
      icon: <FaMobileAlt />,
      title: 'Platform Development',
      desc: 'Native-quality games for both Android & iOS using Unity/Unreal Engine.',
    },
    {
      icon: <FaGooglePlay />,
      title: 'Google Play Publishing',
      desc: 'Full support for store listing, compliance, and launch on Google Play.',
    },
    {
      icon: <FaApple />,
      title: 'App Store Publishing',
      desc: 'Expertise in Apple guidelines, TestFlight, and App Store optimization.',
    },
    {
      icon: <FaPaintBrush />,
      title: 'Mobile-First UI/UX',
      desc: 'Intuitive touch controls and interfaces designed for small screens.',
    },
    {
      icon: <FaCode />,
      title: 'Custom SDK Integration',
      desc: 'Ads, analytics, API, and third-party services seamlessly integrated.',
    },
    {
      icon: <FaRocket />,
      title: 'Performance Optimization',
      desc: 'Smooth 60 FPS gameplay even on older devices – battery efficient.',
    },
    {
      icon: <FaBug />,
      title: 'Device Compatibility Testing',
      desc: 'Tested on hundreds of Android & iOS devices for flawless experience.',
    },
    {
      icon: <FaChartLine />,
      title: 'Monetization Strategy',
      desc: 'Maximize revenue with ads, subscriptions, and in-app purchases.',
    },
  ];

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 30 + 10,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 5,
    opacity: Math.random() * 0.3 + 0.1,
  }));

  return (
    <>
      <style>{framerStyles}</style>
      <section className="w-full bg-gradient-to-br from-white via-gray-50 to-white text-gray-900 font-sans overflow-hidden relative py-16 md:py-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-100/20 rounded-full blur-3xl" />
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full bg-gradient-to-r from-red-400/20 to-rose-400/20 blur-xl animate-float"
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                top: `${particle.top}%`,
                left: `${particle.left}%`,
                opacity: particle.opacity,
                animation: `float ${particle.duration}s ease-in-out infinite`,
                animationDelay: `${particle.delay}s`,
              }}
            />
          ))}
        </div>

        <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Header – underline removed */}
          <MotionOrDiv
            {...(!isMobile && {
              initial: { opacity: 0, y: -30 },
              animate: inView ? { opacity: 1, y: 0 } : {},
              transition: { duration: 0.6 },
            })}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
                Android & iOS Game Development
              </span>
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-lg">
              We build games that dominate both app stores – from concept to live operations.
            </p>
          </MotionOrDiv>

          {/* Cards Grid – no "Learn more" links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, idx) => (
              <MotionOrDiv
                key={idx}
                {...(!isMobile && {
                  initial: { opacity: 0, y: 30 },
                  animate: inView ? { opacity: 1, y: 0 } : {},
                  transition: { duration: 0.5, delay: idx * 0.1 },
                })}
                className="group relative"
              >
                <div className="relative bg-white/50 backdrop-blur-xl rounded-2xl p-6 text-center transition-all duration-500 hover:bg-white/70 hover:scale-105 hover:shadow-2xl hover:shadow-red-200/40 border border-white/20 hover:border-red-400/50 h-full flex flex-col">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/0 via-rose-500/0 to-red-500/0 group-hover:from-red-500/10 group-hover:via-rose-500/10 group-hover:to-red-500/10 transition-all duration-500" />
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="text-4xl text-red-600 mb-4 group-hover:scale-110 transition-transform mx-auto">
                      {s.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 framer-text">
                      {s.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed framer-text">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </MotionOrDiv>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-15px) translateX(5px); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Services;